import React, {useCallback, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {Button, Layout, Page, Text} from '@phonectas/unifon-ui-kit';
import {useLocation, useNavigate} from 'react-router-dom';
import UnauthorizedIcon from 'common/components/icons/UnauthorizedIcon';
import useUserStore from 'common/stores/user';
import PermissionManager from 'common/components/permissions/PermissionManager';
import {ROLE_LABELS} from 'common/components/permissions/constants';
import {ROUTES} from 'common/constants';


const Unauthorized: React.FC = () => {
	const [t] = useTranslation();
	const location = useLocation();
	const navigate = useNavigate();
	const user = useUserStore((state) => state.user);
	const logout = useUserStore((state) => state.logout);
	// Only set when App redirected here; empty when the page is opened directly
	const requiredRoles: string[] = (location.state as {requiredRoles?: string[]} | null)?.requiredRoles ?? [];
	/* i18next-extract-disable-next-line */
	const requiredRoleNames = requiredRoles.map((role) => t(ROLE_LABELS[role] ?? role)).join(', ');
	// Where "Go to start page" leads; hidden when the user has no section at all
	const startRoute = useMemo(() => {
		if (!user) return null;
		const route = new PermissionManager(user).getDefaultRoute();
		return route === ROUTES.UNAUTHORIZED_USER ? null : route;
	}, [user]);
	// Via '/', so App applies the saved landing page as well as the role-based default
	const goToStart = useCallback(() => {
		navigate('/', {replace: true});
	}, [navigate]);
	// A new login picks up roles granted since the last one
	const logInAgain = useCallback(() => {
		logout();
	}, [logout]);

	return (
		<Page style={{overflow: 'hidden'}}>
			<Page.Section
				style={{
					marginTop: '50px',
					overflow: 'hidden',
					height: 'fit-content',
				}}
			>
				<Layout
					gap="xl"
					fullWidth
					fullHeight
					direction="column"
				>
					<Layout fullWidth direction="column" alignItems="center" nowrap justifyContent="center">
						<UnauthorizedIcon />
						<Text variant='h1'>{t('Unauthorized')}</Text>
					</Layout>
					<Layout
						fullWidth
						justifyContent="center"
						direction="column"
						alignItems='center'

					>
						<Text variant="body1">
							{t('You are not authorized to view this page.')}
						</Text>
						{
							requiredRoleNames && (
								<Text variant="body1">
									{t('Required roles: {{requiredRoles}}', {requiredRoles: requiredRoleNames})}
								</Text>
							)
						}
						<Text variant="body1">
							{t('Ask your administrator for access or if you have been given rights, please log in again.')}
						</Text>
					</Layout>
					<Layout fullWidth justifyContent="center" gap="sm">
						{
							startRoute && (
								<Button variant="secondary" onClick={goToStart}>{t('Go to start page')}</Button>
							)
						}
						<Button variant="primary" onClick={logInAgain}>{t('Log in again')}</Button>
					</Layout>
				</Layout>
			</Page.Section>
		</Page>
	);
}

export default Unauthorized;
